import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { organizationApi } from '../../../../services/api';
import Modal from '../../../common/Modal';

/**
 * ADR-124 — Delete Cluster dialog.
 *
 * A cluster may be deleted only when nothing outside its own creation
 * footprint references it. The server owns that rule: this dialog renders the
 * preview it returns and never decides on its own whether the cluster may go.
 *
 * Two states:
 *   blocked   — every reason listed at once, Delete disabled.
 *   deletable — what will be erased, Delete enabled.
 *
 * A 400/409 on confirm means the preview went stale between opening and
 * confirming; the dialog re-renders the fresh blockers in place rather than
 * closing with a toast.
 */

const ClusterDeleteDialog = ({ isOpen, cluster, onClose, onDeleted }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const clusterId = cluster?.id;
  const clusterName = cluster?.name || '';

  const loadPreview = useCallback(async () => {
    if (!clusterId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    setPreview(null);
    try {
      const result = await organizationApi.getClusterDeletePreview(clusterId);
      setPreview(result);
    } catch (err) {
      setError(err.message || 'Failed to check whether this cluster can be deleted');
    } finally {
      setIsLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    } else {
      setPreview(null);
      setError(null);
      setIsDeleting(false);
    }
  }, [isOpen, loadPreview]);

  const handleDelete = async () => {
    if (!clusterId) {
      return;
    }
    setIsDeleting(true);
    setError(null);
    try {
      await organizationApi.deleteCluster(clusterId);
      onDeleted(clusterName);
    } catch (err) {
      // Stale preview: show the fresh blockers in place, keep the dialog open.
      const blockers = err.data?.blockers;
      if (blockers && blockers.length > 0) {
        setPreview((current) => ({ ...(current || {}), deletable: false, blockers }));
      } else {
        setError(err.message || 'Failed to delete cluster');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const blockers = preview?.blockers || [];
  const isBlocked = Boolean(preview) && !preview.deletable;
  const canDelete = Boolean(preview?.deletable) && !isDeleting && !isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Delete ${clusterName}`}
      onSubmit={canDelete ? handleDelete : undefined}
      submitText={isDeleting ? 'Deleting…' : 'Delete'}
      submitDisabled={!canDelete}
      cancelText={isBlocked ? 'Close' : 'Cancel'}
      width="640px"
    >
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Checking whether this cluster can be deleted…</Typography>
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!isLoading && isBlocked && (
        <Alert severity="warning" icon={<WarningAmberIcon />}>
          <AlertTitle>This cluster is in use</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Only a cluster that was never used can be deleted. Resolve the following first:
          </Typography>
          <List dense disablePadding>
            {blockers.map((blocker) => (
              <ListItem key={blocker.code} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 28, mt: '2px' }}>
                  <WarningAmberIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary={blocker.message} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {!isLoading && preview?.deletable && (
        <Alert severity="error" icon={<DeleteForeverIcon />}>
          <AlertTitle>This cluster has never been used</AlertTitle>
          <Typography variant="body2">
            Deleting <strong>{clusterName}</strong> also removes
            {preview.willErase?.defaultBusinessUnit
              ? <> its default business unit <strong>{preview.willErase.defaultBusinessUnit.name}</strong> and</>
              : ''}
            {' '}{preview.willErase?.edgeRows ?? 0} structure-history row
            {(preview.willErase?.edgeRows ?? 0) === 1 ? '' : 's'}. This cannot be undone.
          </Typography>
        </Alert>
      )}
    </Modal>
  );
};

ClusterDeleteDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  cluster: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired
};

ClusterDeleteDialog.defaultProps = {
  cluster: null
};

export default ClusterDeleteDialog;
